import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from './PrimaryActionButton';
import SupportTypingDots from './SupportTypingDots';
import { isVisitorWidgetOwnMessage } from '../../utils/supportChatThread';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const SupportChatPanel = ({
  open,
  onClose,
  messages,
  draft,
  onDraftChange,
  onSend,
  sending,
  guestEmail,
  onGuestEmailChange,
  showGuestEmail,
  status,
  error,
  dockAboveBottomNav = false,
  peerTyping = false,
  isStaffWidget = false,
  isStoreChat = false,
  storeName = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const listRef = useRef(null);
  const peerName = isStoreChat
    ? (storeName || t('supportChat.store'))
    : t('supportChat.support');

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open, peerTyping]);

  if (!open) {
    return null;
  }

  return createPortal(
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        zIndex: 13000,
        right: { xs: 8, sm: 24 },
        bottom: dockAboveBottomNav && isCompact
          ? 'calc(96px + env(safe-area-inset-bottom, 0px))'
          : { xs: 8, sm: 24 },
        width: isCompact ? 'calc(100% - 16px)' : 380,
        maxWidth: 420,
        height: isCompact ? 'min(72vh, 640px)' : 520,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${BRAND.line}`,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: BRAND.primary,
          color: BRAND.onPrimary,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t(isStoreChat ? 'supportChat.storeTitle' : 'supportChat.title')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('common.close')} sx={{ color: BRAND.onPrimary }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, bgcolor: BRAND.background }}>
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t(isStoreChat ? 'supportChat.storeEmpty' : 'supportChat.empty')}
          </Typography>
        ) : (
          messages.map((message) => {
            const isOwn = isVisitorWidgetOwnMessage(message.senderKind, isStaffWidget);
            return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: isOwn ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '80%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: isOwn ? BRAND.accent : BRAND.surface,
                    color: isOwn ? BRAND.onAccent : BRAND.text,
                    border: isOwn ? 'none' : `1px solid ${BRAND.line}`,
                    boxShadow: 'none',
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, mb: 0.25 }}>
                    {isOwn ? t('supportChat.you') : peerName}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.body}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
        {peerTyping ? (
          <SupportTypingDots
            label={t(isStoreChat ? 'supportChat.storeTyping' : 'supportChat.supportTyping', { name: peerName })}
          />
        ) : null}
        {status === 'Waiting' && !peerTyping ? (
          <Typography variant="caption" color="text.secondary">
            {t('supportChat.waiting')}
          </Typography>
        ) : null}
      </Box>

      {status === 'Closed' ? (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            bgcolor: brandAlpha('accent', 0.12),
            borderTop: `1px solid ${brandAlpha('accent', 0.25)}`,
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" sx={{ color: BRAND.accentText, fontWeight: 600 }}>
            {t('supportChat.closed')}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ p: 1.5, borderTop: `1px solid ${BRAND.line}`, flexShrink: 0, bgcolor: BRAND.surface }}>
        {showGuestEmail ? (
          <TextField
            fullWidth
            size="small"
            type="email"
            value={guestEmail}
            onChange={(event) => onGuestEmailChange(event.target.value)}
            placeholder={t('supportChat.guestEmail')}
            helperText={t('supportChat.guestEmailHint')}
            sx={{ mb: 1 }}
          />
        ) : null}
        {error ? (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
            {error}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder={status === 'Closed' ? t('supportChat.closedPlaceholder') : t('supportChat.placeholder')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                onSend();
              }
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                height: 48,
              },
            }}
          />
          <PrimaryActionButton
            onClick={onSend}
            disabled={sending || !draft.trim()}
            sx={{
              minWidth: 48,
              width: 48,
              height: 48,
              minHeight: 48,
              px: 0,
              flexShrink: 0,
              alignSelf: 'center',
            }}
          >
            <SendIcon fontSize="small" />
          </PrimaryActionButton>
        </Box>
      </Box>
    </Paper>,
    document.body,
  );
};

export default SupportChatPanel;
